import { useRef } from "react";
import { Globe2, Languages, Mic, MicOff, RadioTower, Square, Sparkles, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { AionVisualPresence } from "@/components/aion/AionVisualPresence";
import { AionHolographicFace } from "@/components/aion/AionHolographicFace";
import { getWakeWordModeLabel, getVoiceStateCopy, normalizeVoiceState, normalizeWakeWordMode, VOICE_STATES, WAKE_WORD_MODES } from "@/constants/voiceRuntime";

export const VOICE_LANGUAGE_OPTIONS = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "Englisch" },
  { value: "sq", label: "Albanisch" },
  { value: "tr", label: "Türkisch" },
  { value: "fr", label: "Französisch" },
  { value: "es", label: "Spanisch" },
  { value: "it", label: "Italienisch" },
  { value: "ar", label: "Arabisch" },
  { value: "nl", label: "Niederländisch" },
  { value: "pt", label: "Portugiesisch" },
  { value: "pl", label: "Polnisch" },
  { value: "ru", label: "Russisch" },
  { value: "sv", label: "Schwedisch" },
  { value: "no", label: "Norwegisch" },
  { value: "da", label: "Dänisch" },
  { value: "fi", label: "Finnisch" },
  { value: "el", label: "Griechisch" },
  { value: "sr", label: "Serbisch" },
  { value: "hr", label: "Kroatisch" },
  { value: "bs", label: "Bosnisch" },
  { value: "ro", label: "Rumänisch" },
  { value: "bg", label: "Bulgarisch" },
  { value: "zh", label: "Chinesisch (vereinfacht)" },
  { value: "ja", label: "Japanisch" },
  { value: "ko", label: "Koreanisch" },
  { value: "uk", label: "Ukrainisch" },
  { value: "cs", label: "Tschechisch" },
  { value: "hu", label: "Ungarisch" },
  { value: "hi", label: "Hindi" },
  { value: "id", label: "Indonesisch" },
  { value: "ms", label: "Malaiisch" },
  { value: "vi", label: "Vietnamesisch" },
  { value: "th", label: "Thailändisch" },
  { value: "he", label: "Hebräisch" },
  { value: "fa", label: "Persisch" },
  { value: "ur", label: "Urdu" },
  { value: "bn", label: "Bengalisch" },
  { value: "sw", label: "Swahili" },
];

const EXAMPLE_PROMPTS = [
  "AION, was ist heute wichtig?",
  "AION, erinnere mich an meine Entscheidung zu Projekt X.",
  "AION, schalte das Licht im Wohnzimmer aus.",
  "AION, was habe ich gestern beschlossen?",
];

const VOICE_PROFILE_LOCK_STATES = new Set([
  VOICE_STATES.LISTENING,
  VOICE_STATES.SPEECH_DETECTED,
  VOICE_STATES.TRANSCRIBING,
  VOICE_STATES.THINKING,
  VOICE_STATES.EXECUTING,
  VOICE_STATES.RESPONDING,
  VOICE_STATES.SPEAKING,
]);

const resolveAvatarExpression = (transcript = "") => {
  const normalized = String(transcript).toLocaleLowerCase();
  const rules = [
    { expression: "stern", patterns: ["grimmig", "streng", "ernst", "stern face", "angry face"] },
    { expression: "warm", patterns: ["lächel", "laechel", "smile", "freundlich"] },
    { expression: "surprised", patterns: ["überrascht", "ueberrascht", "surprised"] },
    { expression: "calm", patterns: ["neutral", "ruhig", "calm"] },
  ];
  return rules.find(({ patterns }) => patterns.some((pattern) => normalized.includes(pattern)))?.expression;
};

export const AionView = ({
  assistantVoiceProfile,
  audioInputLevel,
  handsFreeMicEnabled,
  audioOutputLevel,
  memory,
  memoryRecallResult,
  memoryTopics,
  onExamplePrompt,
  onLanguageSave,
  onStartWakeWord,
  onToggleHandsFree,
  onStopVoiceOutput,
  onStopWakeWord,
  onToggleLanguage,
  onToggleMute,
  onVoiceProfileSave,
  selectedVoiceLanguages,
  vadSupported,
  voiceDisplay,
  voiceError,
  voiceLanguageMode,
  voiceLanguageModeLabel,
  voiceMuted,
  voicePartialTranscript,
  voicePending,
  voiceResearch,
  voiceSessionId,
  voiceStatus,
  wakeWordArmed,
  wakeWordHeard,
  wakeWordSupported,
  voiceCaptureSupported = true,
}) => {
  const responseCardRef = useRef(null);
  const recallMemories = memoryRecallResult?.memories || [];
  const topics = memoryTopics?.topics || [];
  const state = normalizeVoiceState(voiceStatus.voice_state);
  const voiceProfileLocked = Boolean(voicePending) || VOICE_PROFILE_LOCK_STATES.has(state);
  const wakeWordMode = normalizeWakeWordMode(voiceStatus.wake_word_mode);
  const wakeWordLabel = getWakeWordModeLabel(wakeWordMode);
  const primaryLanguage = selectedVoiceLanguages[0] || memory?.preferred_language || memory?.preferred_languages?.[0] || "de";
  const voiceConfigured = voiceStatus.voice_ready && voiceStatus.stt_ready && voiceStatus.tts_ready;
  const stateCopy = voiceConfigured ? getVoiceStateCopy(state, { hasError: Boolean(voiceError), wakeWord: voiceStatus.wake_word || "AION" }) : { title: "Voice nicht verbunden", subtitle: "Der Sprachdienst ist nicht verfügbar. Du kannst weiterhin Text eingeben." };
  const roomContext = voiceStatus.room_context || {};
  const avatarExpression = resolveAvatarExpression(voiceDisplay?.transcript);
  const researchStatusLabel = {
    AVAILABLE: "Live aktiv",
    DEGRADED: "Eingeschränkt",
    UNAVAILABLE: "Nicht verfügbar",
  }[voiceResearch?.provider_state] || "Nicht aktiv";
  const researchFreshnessLabel = {
    real_time: "Live",
    recent: "Aktuell",
    evergreen: "Grundlage",
  }[voiceResearch?.freshness] || "—";

  return (
    <div className="aion-view aion-view-stack" data-testid="aion-view">
      <section className="aion-hero-card" data-testid="aion-conversation-hero">
        <div className="aion-voice-hero-grid" data-testid="aion-voice-hero-grid">
          <div className="aion-voice-hero-copy" data-testid="aion-voice-hero-copy">
            <p className="aion-hero-kicker" data-testid="voice-assistant-title">AION</p>
            <h1 className="aion-hero-title" data-testid="aion-conversation-title">{stateCopy.title}</h1>
            <p className="aion-hero-copy" data-testid="voice-assistant-subtitle">{stateCopy.subtitle}</p>

            <div className="aion-example-grid" data-testid="aion-example-grid">
              {EXAMPLE_PROMPTS.map((prompt, index) => (
                <button type="button" className="aion-example-chip" key={prompt} onClick={() => onExamplePrompt(prompt)} data-testid={`aion-example-prompt-${index}`}>
                  {prompt}
                </button>
              ))}
            </div>

            <div className="aion-inline-stats" data-testid="voice-runtime-grid">
              <div className="aion-inline-stat" data-testid="voice-runtime-state"><span>Status</span><strong>{voiceStatus.voice_ready && voiceStatus.stt_ready && voiceStatus.tts_ready ? (state === "IDLE" ? "Voice konfiguriert" : voiceStatus.display_state) : "Voice nicht verbunden"}</strong></div>
              <div className="aion-inline-stat" data-testid="voice-runtime-mic"><span>Mikrofon</span><strong>{!voiceCaptureSupported ? "Nicht verfügbar" : voiceMuted ? "Aus" : voiceStatus.privacy?.mic === "MIC_ON" ? "An" : "Bereit"}</strong></div>
              <div className="aion-inline-stat" data-testid="voice-runtime-wake-word"><span>Aktivierung</span><strong>{wakeWordLabel}</strong></div>
              <div className="aion-inline-stat" data-testid="voice-runtime-language"><span>Sprachen</span><strong>{primaryLanguage}</strong></div>
            </div>

            {roomContext?.room_name ? (
              <div className="aion-note" data-testid="voice-room-context-note">Bereichskontext aktiv: {roomContext.room_name}</div>
            ) : null}
          </div>

          <div className="aion-voice-core-panel" data-testid="aion-voice-core-panel">
            <div className="aion-assistant-identity" data-testid="aion-assistant-identity">
              <div>
                <span className="aion-assistant-identity-kicker">AI ASSISTANT</span>
                <strong>{assistantVoiceProfile === "male" ? "ORION" : "ASTRA"}</strong>
              </div>
              <span className={`aion-assistant-live ${voiceConfigured ? "is-live" : ""}`}>
                {voiceConfigured ? "LIVE" : "OFFLINE"}
              </span>
            </div>
            <div className="aion-conversation-avatar-stage" data-testid="aion-conversation-avatar-stage">
              <button
                type="button"
                className={`aion-assistant-selector aion-assistant-selector-female ${assistantVoiceProfile === "female" ? "active" : ""}`}
                aria-pressed={assistantVoiceProfile === "female"}
                disabled={voiceProfileLocked}
                onClick={() => onVoiceProfileSave?.("female")}
                data-testid="aion-assistant-select-female"
              >
                <span className="aion-assistant-selector-orb"><AionHolographicFace profile="female" expression="warm" /></span>
                <span><strong>ASTRA</strong><small>Weibliche Stimme</small></span>
                <span className="aion-assistant-selector-state">{assistantVoiceProfile === "female" ? "AKTIV" : voiceProfileLocked ? "WARTEN" : "WÄHLEN"}</span>
              </button>

              <AionVisualPresence
                audioInputLevel={audioInputLevel}
                audioOutputLevel={audioOutputLevel}
                expression={avatarExpression}
                avatarProfile={assistantVoiceProfile}
                state={state}
                statusLabel={voiceStatus.voice_ready && voiceStatus.stt_ready && voiceStatus.tts_ready ? (state === "IDLE" ? "Voice konfiguriert" : voiceStatus.display_state) : "Voice nicht verbunden"}
                subtitle={voicePartialTranscript ? `Zuletzt erkannt: ${voicePartialTranscript}` : stateCopy.subtitle}
                title={stateCopy.title}
                wakeWord={voiceStatus.wake_word || "AION"}
              />

              <button
                type="button"
                className={`aion-assistant-selector aion-assistant-selector-male ${assistantVoiceProfile === "male" ? "active" : ""}`}
                aria-pressed={assistantVoiceProfile === "male"}
                disabled={voiceProfileLocked}
                onClick={() => onVoiceProfileSave?.("male")}
                data-testid="aion-assistant-select-male"
              >
                <span className="aion-assistant-selector-orb"><AionHolographicFace profile="male" expression="calm" /></span>
                <span><strong>ORION</strong><small>Männliche Stimme</small></span>
                <span className="aion-assistant-selector-state">{assistantVoiceProfile === "male" ? "AKTIV" : voiceProfileLocked ? "WARTEN" : "WÄHLEN"}</span>
              </button>
            </div>
            <div className="aion-assistant-question aion-assistant-question-conversation" data-testid="aion-assistant-question">
              <span>FRAG MICH ETWAS</span>
              <strong>{state === VOICE_STATES.LISTENING ? "Ich höre dir zu …" : stateCopy.title}</strong>
              <small>{voicePartialTranscript ? `Du: ${voicePartialTranscript}` : stateCopy.subtitle}</small>
            </div>

            <div className="aion-voice-control-row" data-testid="aion-voice-control-row">
              <Button
                type="button"
                variant={wakeWordArmed || handsFreeMicEnabled ? "default" : "outline"}
                onClick={wakeWordArmed ? onStopWakeWord : handsFreeMicEnabled ? onToggleHandsFree : wakeWordSupported ? onStartWakeWord : onToggleHandsFree}
                disabled={voiceMuted || voicePending || Boolean(voiceStatus.privacy?.policy_blocked) || (!wakeWordSupported && !voiceCaptureSupported)}
                aria-disabled={voiceMuted || voicePending || Boolean(voiceStatus.privacy?.policy_blocked) || (!wakeWordSupported && !voiceCaptureSupported)}
                title={voiceStatus.privacy?.policy_blocked ? "Mikrofon ist im Trust Center gesperrt." : undefined}
                data-testid="aion-wake-word-toggle-button"
              >
                <RadioTower className="aion-button-icon" aria-hidden="true" />
                {wakeWordArmed || handsFreeMicEnabled
                  ? "Sprachmodus beenden"
                  : voiceStatus.privacy?.policy_blocked
                    ? "Im Trust Center gesperrt"
                    : (!wakeWordSupported && !voiceCaptureSupported)
                      ? "Mikrofon nicht verfügbar"
                      : "AION aktivieren"}
              </Button>
              <Button
                type="button"
                variant={voiceMuted ? "default" : "outline"}
                onClick={onToggleMute}
                disabled={voicePending || !voiceCaptureSupported || Boolean(voiceStatus.privacy?.policy_blocked)}
                aria-disabled={voicePending || !voiceCaptureSupported || Boolean(voiceStatus.privacy?.policy_blocked)}
                title={voiceStatus.privacy?.policy_blocked
                  ? "Mikrofon ist im Trust Center gesperrt."
                  : !voiceCaptureSupported
                    ? "Mikrofonaufnahme wird von diesem Browser nicht unterstützt."
                    : undefined}
                data-testid="aion-voice-mute-toggle-button"
              >
                {voiceMuted ? <MicOff className="aion-button-icon" aria-hidden="true" /> : <Mic className="aion-button-icon" aria-hidden="true" />}
                {voiceStatus.privacy?.policy_blocked
                  ? "Im Trust Center gesperrt"
                  : !voiceCaptureSupported
                    ? "Mikrofon nicht verfügbar"
                    : voiceMuted
                      ? "Mikrofon aktivieren"
                      : "Mikrofon aus"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onStopVoiceOutput({ notifyBackend: true, reason: "manual_stop_button", nextState: wakeWordArmed ? VOICE_STATES.WAKE : (voiceMuted ? VOICE_STATES.MUTED : VOICE_STATES.IDLE), nextWakeWordMode: wakeWordArmed ? WAKE_WORD_MODES.BROWSER : WAKE_WORD_MODES.DISABLED })}
                disabled={!([VOICE_STATES.SPEAKING, VOICE_STATES.LISTENING, VOICE_STATES.SPEECH_DETECTED, VOICE_STATES.WAKE, VOICE_STATES.TRANSCRIBING, VOICE_STATES.THINKING, VOICE_STATES.RESPONDING, VOICE_STATES.EXECUTING].includes(state))}
                data-testid="aion-voice-stop-button"
              >
                <Square className="aion-button-icon" aria-hidden="true" />
                Stopp
              </Button>
            </div>

            <div className="aion-voice-mode-note" data-testid="aion-voice-mode-note">
              {voiceStatus.privacy?.policy_blocked
                ? "Mikrofon ist im Trust Center gesperrt. Live-Sprachsteuerung bleibt aus, bis die Freigabe dort wieder aktiviert wird."
                : handsFreeMicEnabled
                  ? "Freisprechen aktiv · AION hört nach jeder Antwort automatisch weiter zu."
                  : wakeWordArmed
                    ? `Sprachsteuerung aktiv${wakeWordHeard ? ` · zuletzt gehört: ${wakeWordHeard}` : ""}`
                    : wakeWordSupported
                      ? "Sprachsteuerung ist verfügbar, aber aktuell aus."
                      : voiceCaptureSupported
                        ? "Dieser Browser nutzt automatisch den Freisprech-Modus statt dauerhaftem Browser-Weckwort."
                        : "Live-Mikrofon ist in diesem Browser nicht verfügbar. Nutze Texteingabe oder Sprachmemo."}
            </div>
          </div>
        </div>
      </section>

      {voiceError ? <div className="aion-error-box" data-testid="voice-error-box">{voiceError}</div> : null}

      <div className="aion-two-column" data-testid="aion-response-grid">
        <AionSectionCard
          title="Letzte Antwort"
          eyebrow="AION ANTWORT"
          subtitle="Die zentrale Unterhaltung bleibt hier sichtbar – inklusive Sprache, Absicht und Session-Kontext."
          testId="aion-response-card"
        >
          {(voiceDisplay.response || voiceDisplay.transcript || voicePending) ? (
            <>
              <div ref={responseCardRef} />
              {voiceDisplay.transcript ? <div className="aion-note" data-testid="voice-last-user-turn">Du: {voiceDisplay.transcript}</div> : null}
              {voiceDisplay.response ? (
                <div className="aion-response-box" data-testid="voice-response-box">{voiceDisplay.response}</div>
              ) : (
                <div className="aion-note" data-testid="voice-pending-response-box">AION verarbeitet gerade deine Sprache …</div>
              )}
              {voiceDisplay.memoryNotice ? (
                <div className="aion-note aion-memory-learning-note" data-testid="voice-memory-learned-note">
                  <span className="aion-status-chip" data-testid="voice-memory-learned-label">Neu gelernt</span>
                  <strong data-testid="voice-memory-learned-text">{voiceDisplay.memoryNotice}</strong>
                </div>
              ) : null}
              <div className="aion-inline-stats" data-testid="voice-result-grid">
                <div className="aion-inline-stat" data-testid="voice-language-box"><span>Sprache</span><strong>{voiceDisplay.language || primaryLanguage || "—"}</strong></div>
                <div className="aion-inline-stat" data-testid="voice-intent-box"><span>Absicht</span><strong>{voiceDisplay.intent || "—"}</strong></div>
                <div className="aion-inline-stat" data-testid="voice-state-box"><span>Status</span><strong>{voiceStatus.voice_ready && voiceStatus.stt_ready && voiceStatus.tts_ready ? (state === "IDLE" ? "Voice konfiguriert" : voiceStatus.display_state) : "Voice nicht verbunden"}</strong></div>
                <div className="aion-inline-stat" data-testid="voice-fast-path-box"><span>Schnellreaktion</span><strong>{voiceStatus.fast_path_used ? "aktiv" : "normal"}</strong></div>
                <div className="aion-inline-stat" data-testid="voice-audio-input-box"><span>Mikrofonpegel</span><strong>{Math.round((audioInputLevel || 0) * 100)}%</strong></div>
                <div className="aion-inline-stat" data-testid="voice-audio-output-box"><span>Stimmpegel</span><strong>{Math.round((audioOutputLevel || 0) * 100)}%</strong></div>
              </div>
              <div className="aion-note" data-testid="voice-wake-word-note">
                {wakeWordArmed
                  ? `Sag einfach „${voiceStatus.wake_word || "AION"} …“. ${wakeWordHeard ? `Zuletzt erkannt: ${wakeWordHeard}.` : "Ich warte auf dein Aktivierungswort."}`
                  : "Text bleibt immer verfügbar – und über AION aktivieren kannst du das Browser-Weckwort einschalten."}
              </div>
              {voicePartialTranscript ? <div className="aion-note aion-partial-note" data-testid="voice-partial-transcript-note">Teiltranskript: {voicePartialTranscript}</div> : null}
              {voiceResearch?.used ? (
                <div className="aion-research-block" data-testid="aion-research-block">
                  <div className="aion-research-header" data-testid="aion-research-header">
                    <div>
                      <strong data-testid="aion-research-title">Recherche</strong>
                      <small data-testid="aion-research-summary">Öffentliche Quellen bleiben getrennt vom Langzeitspeicher.</small>
                    </div>
                    <div className="aion-research-badges" data-testid="aion-research-badges">
                      <span className="aion-status-chip" data-testid="aion-research-provider-state">{researchStatusLabel}</span>
                      <span className="aion-status-chip" data-testid="aion-research-freshness">{researchFreshnessLabel}</span>
                      <span className="aion-status-chip" data-testid="aion-research-confidence">{Math.round((voiceResearch.confidence || 0) * 100)}%</span>
                    </div>
                  </div>

                  {voiceResearch.sources?.length ? (
                    <div className="aion-source-grid" data-testid="aion-research-source-grid">
                      {voiceResearch.sources.map((source, index) => (
                        <a
                          key={`${source.url}-${index}`}
                          className="aion-source-card"
                          href={source.url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`aion-research-source-link-${index}`}
                        >
                          <div className="aion-source-card-header">
                            <strong data-testid={`aion-research-source-title-${index}`}>[{index + 1}] {source.title}</strong>
                            <span data-testid={`aion-research-source-domain-${index}`}>{source.domain}</span>
                          </div>
                          <p data-testid={`aion-research-source-snippet-${index}`}>{source.snippet || "Keine kurze Vorschau verfügbar."}</p>
                          <div className="aion-source-meta" data-testid={`aion-research-source-meta-${index}`}>
                            <span>Autorität {Math.round((source.authority_score || 0) * 100)}%</span>
                            <span>Frische {Math.round((source.freshness_score || 0) * 100)}%</span>
                            <span>Vertrauen {Math.round((source.confidence || 0) * 100)}%</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="aion-note" data-testid="aion-research-empty-note">
                      {voiceResearch.answer || "Live-Recherche war für diese Antwort nicht verfügbar."}
                    </div>
                  )}

                  {voiceResearch.conflicts?.length ? (
                    <div className="aion-warning-box" data-testid="aion-research-conflict-box">
                      {voiceResearch.conflicts[0]?.summary}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <AionEmptyState icon={Sparkles} title="AION ist bereit." description="Sprich oder schreibe eine Anfrage – die Antwort erscheint hier mit echtem Session-Kontext." testId="aion-response-empty" />
          )}
        </AionSectionCard>

        <AionSectionCard
          title="Was erinnert sich AION gerade?"
          eyebrow="GESPRÄCHSSPEICHER"
          subtitle="Echte Gesprächserinnerungen aus dem vorhandenen Gesprächsspeicher."
          testId="aion-session-memory-card"
        >
          {recallMemories.length ? (
            <div className="aion-list" data-testid="aion-session-memory-list">
              {recallMemories.map((item, index) => (
                <div className="aion-list-row" key={`${item.timestamp || index}-${index}`} data-testid={`aion-session-memory-${index}`}>
                  <div>
                    <strong>{item.role === "assistant" ? "AION" : "Du"}</strong>
                    <small>{item.message}</small>
                  </div>
                  <span>{item.language || "de"}</span>
                </div>
              ))}
            </div>
          ) : (
            <AionEmptyState icon={Mic} title="Noch keine Session-Erinnerungen." description="Sobald du mit AION sprichst, erscheinen hier echte Gesprächsausschnitte." testId="aion-session-memory-empty" />
          )}
        </AionSectionCard>
      </div>

      <AionSectionCard
        title="Aktive Themen"
        eyebrow="MEMORY THEMEN"
        subtitle="Themen, die AION aktuell in deinen Erinnerungen erkennt."
        testId="aion-active-topics-card"
      >
        {topics.length ? (
          <div className="aion-topic-grid" data-testid="aion-active-topics-grid">
            {topics.slice(0, 8).map((topic, index) => (
              <div className="aion-topic-card" key={topic.id || `${topic.name}-${index}`} data-testid={`aion-topic-${index}`}>
                <div className="aion-topic-title"><Languages className="aion-button-icon" aria-hidden="true" />{topic.name}</div>
                <small>{topic.topic_type} · {Math.round((topic.confidence || 0) * 100)}%</small>
              </div>
            ))}
          </div>
        ) : (
          <AionEmptyState icon={Globe2} title="Noch keine aktiven Themen." description="AION zeigt hier nur echte Topic-Signale aus deiner Nutzung." testId="aion-topics-empty" />
        )}
      </AionSectionCard>

      <AionSectionCard title="Voice V2 Status" eyebrow="LAUFZEIT" subtitle="Klarer Status für Aktivierung, Unterbrechung und Antwortfluss." testId="aion-runtime-status-card">
        <div className="aion-inline-stats" data-testid="aion-runtime-status-grid">
          <div className="aion-inline-stat" data-testid="voice-runtime-provider-box"><span>Weckwort</span><strong>{wakeWordLabel}</strong></div>
          <div className="aion-inline-stat" data-testid="voice-runtime-support-box"><span>Browser</span><strong>{wakeWordSupported ? "bereit" : "eingeschränkt"}</strong></div>
          <div className="aion-inline-stat" data-testid="voice-runtime-tts-box"><span>Antwortlautstärke</span><strong>{state === VOICE_STATES.SPEAKING ? "reaktiv" : "bereit"}</strong></div>
          <div className="aion-inline-stat" data-testid="voice-runtime-vad-box"><span>Sprachende</span><strong>{vadSupported ? "VAD aktiv" : "Fallback"}</strong></div>
          <div className="aion-inline-stat" data-testid="voice-runtime-mute-box"><span>Privatsphäre</span><strong>{voiceMuted ? "Mikrofon aus" : "aktiv"}</strong></div>
          <div className="aion-inline-stat" data-testid="voice-runtime-output-box"><span>Audio</span><strong>{state === VOICE_STATES.SPEAKING ? <><Volume2 className="aion-button-icon" aria-hidden="true" /> Live</> : "still"}</strong></div>
        </div>
      </AionSectionCard>
    </div>
  );
};
